import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { ElMessage } from 'element-plus'
import { export_newComplateNum } from '@/api/export'
import { trackPageView } from '@/utils/auditTrack'
import { useCache } from '@/hooks/web/useCache'
import { useI18n } from '@/hooks/web/useI18n'
import { useAppStoreWithOut } from '@/store/modules/app'
import { Layout } from '@/utils/routerHelper'

const { t } = useI18n()
const { wsCache } = useCache()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dataCenter/index',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'RedirectPage',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/system/auditLog',
    component: Layout,
    name: 'AuditLogShell',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/System/Audit/AuditLog.vue'),
        name: 'AuditLogStatic',
        meta: {
          hidden: true,
          title: '登录操作审计'
        }
      }
    ]
  },
  {
    path: '/premiss/internalOperatorManagement',
    component: Layout,
    name: 'InternalOperatorManagementShell',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/premiss/InternalOperatorManagement/InternalOperatorManagement.vue'),
        name: 'InternalOperatorManagementStatic',
        meta: {
          hidden: true,
          title: '内部运营管理'
        }
      }
    ]
  },
  {
    path: '/premiss/UserDatas/:id/:token/:platform/:name',
    component: Layout,
    name: 'PremissUserDatasShell',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/premiss/UserDatas/UserDatas.vue'),
        name: 'PremissUserDatasStatic',
        meta: {
          hidden: true,
          title: '用户数据'
        }
      }
    ]
  },
  {
    // 营销触达：活动详情页（隐藏路由壳，入口为列表"详情"）
    path: '/operate/marketingReach/detail/:id',
    component: Layout,
    name: 'MarketingReachDetailShell',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/Operate/MarketingReach/MarketingReachDetail.vue'),
        name: 'MarketingReachDetailStatic',
        meta: {
          hidden: true,
          title: '触达客户详情'
        }
      }
    ]
  },
  {
    // 营销触达：新建群发页（隐藏路由壳，入口为列表"群发"）
    path: '/operate/marketingReach/groupSend/:id',
    component: Layout,
    name: 'MarketingReachGroupSendShell',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/Operate/MarketingReach/GroupSend.vue'),
        name: 'MarketingReachGroupSendStatic',
        meta: {
          hidden: true,
          title: '新建群发'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/views/Error/404.vue'),
    name: 'CatchAll',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

// Business menus are provided by the backend through admin/getRouters.
// Keep this empty so deleted upstream demo pages cannot be injected locally.
export const asyncRouterMap: AppRouteRecordRaw[] = []

const router = createRouter({
  history: createWebHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

const staleAssetReloadKey = 'scrm:stale-asset-reload-at'
const staleAssetReloadQuery = '__scrm_asset_retry'

const isStaleAssetError = (error: unknown) => {
  const message = String((error as Error)?.message || error || '')
  return /Unable to preload CSS|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .* failed|error loading dynamically imported module/i.test(
    message
  )
}

const hasStaleAssetReloadAttempt = () => {
  const url = new URL(window.location.href)
  if (url.searchParams.has(staleAssetReloadQuery)) return true

  try {
    return Boolean(window.sessionStorage.getItem(staleAssetReloadKey))
  } catch (_error) {
    return false
  }
}

const markStaleAssetReloadAttempt = () => {
  try {
    window.sessionStorage.setItem(staleAssetReloadKey, String(Date.now()))
  } catch (_error) {}
}

const getStaleAssetReloadUrl = () => {
  const url = new URL(window.location.href)
  url.searchParams.set(staleAssetReloadQuery, String(Date.now()))
  return url.toString()
}

const clearStaleAssetReloadState = () => {
  try {
    window.sessionStorage.removeItem(staleAssetReloadKey)
  } catch (_error) {}

  const url = new URL(window.location.href)
  if (!url.searchParams.has(staleAssetReloadQuery)) return

  url.searchParams.delete(staleAssetReloadQuery)
  window.history.replaceState(window.history.state, '', url.toString())
}

router.onError((error) => {
  if (!isStaleAssetError(error) || typeof window === 'undefined') return

  if (!hasStaleAssetReloadAttempt()) {
    markStaleAssetReloadAttempt()
    window.location.replace(getStaleAssetReloadUrl())
    return
  }

  ElMessage.warning('系统资源已更新，请刷新页面后继续使用')
})

export const resetRouter = (): void => {
  const resetWhiteNameList = [
    'Redirect',
    'Login',
    'NoFind',
    'CatchAll',
    'Root',
    'AuditLogShell',
    'AuditLogStatic',
    'InternalOperatorManagementShell',
    'InternalOperatorManagementStatic',
    'PremissUserDatasShell',
    'PremissUserDatasStatic'
  ]
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !resetWhiteNameList.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

let lastExportNewComplateNum: number | undefined
let exportNewComplateReqId = 0
let lastRouteFullPath: string | undefined
const visitDataPathPrefix = '/premiss/UserDatas'

router.afterEach((to, from, failure) => {
  if (!failure && typeof window !== 'undefined') {
    clearStaleAssetReloadState()
  }

  if (!to.path.startsWith(visitDataPathPrefix)) {
    wsCache.delete('audit_session_id')
  }
  trackPageView(to, from)
  if (to.fullPath === lastRouteFullPath) return
  lastRouteFullPath = to.fullPath
  try {
    const reqId = ++exportNewComplateReqId
    export_newComplateNum()
      .then((res: any) => {
        if (reqId !== exportNewComplateReqId) return
        if (res && res.code === 1) {
          const num = res.data?.newComplateNum || 0
          const appStore = useAppStoreWithOut()
          appStore.setExportComplateDot(num > 0)
          if (num > 0 && num !== lastExportNewComplateNum) {
            ElMessage('下载完成，右上角头像”导出记录“查看')
          }
          lastExportNewComplateNum = num
        }
      })
      .catch(() => {})
  } catch (error) {}
})

export default router
